import {
  type CreateAdminCompanyDto,
  type UpdateAdminCompanyDto,
  type V2AdminCompaniesPageDto,
  type V2AdminCompaniesSummaryDto,
  type V2AdminCompanyDto,
  v2AdminCompaniesControllerCreate,
  v2AdminCompaniesControllerDetail,
  v2AdminCompaniesControllerList,
  v2AdminCompaniesControllerLookup,
  v2AdminCompaniesControllerRemove,
  v2AdminCompaniesControllerSummary,
  v2AdminCompaniesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminCompaniesPageSchema,
  V2AdminCompaniesSummarySchema,
  V2AdminCompanyLookupSchema,
  V2AdminCompanySchema
} from '@idc-forms/schemas/api-v2/admin-companies'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de empresas contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`cmp_`); la autoría
 * de cada fila viaja por `usr_` y nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020
 * —typechequea igual, así que el bug no aparece hasta filtrar—.
 */

/** Referencia de empresa para selects/filtros v2: `cmp_` + nombre + predeterminada. */
export type CompanyV2LookupOption = { id: string; name: string; default: boolean }

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type CompaniesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type CompaniesAdminV2Row = V2AdminCompaniesPageDto['data'][number]

function toListQuery(query: CompaniesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const companiesAdminV2Service = {
  /** Empresas activas como referencias para selects/filtros v2. */
  lookup: async (accessToken: string): Promise<CompanyV2LookupOption[]> => {
    const result = await v2AdminCompaniesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminCompanyLookupSchema, result.data).data.map((company) => ({
      id: company.id,
      name: company.name,
      default: company.default
    }))
  },

  list: async (
    accessToken: string,
    query: CompaniesAdminV2Query
  ): Promise<V2AdminCompaniesPageDto> => {
    const result = await v2AdminCompaniesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminCompaniesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<CompaniesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminCompaniesSummaryDto> => {
    const result = await v2AdminCompaniesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminCompaniesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminCompanyDto> => {
    const result = await v2AdminCompaniesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminCompanySchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminCompanyDto): Promise<V2AdminCompanyDto> => {
    const result = await v2AdminCompaniesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminCompanyDto
  ): Promise<V2AdminCompanyDto> => {
    const result = await v2AdminCompaniesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminCompaniesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
